import { ImagePaths } from '../../lib/imageUrls.js';

export const smbCybersecurityDataProtectionStrategies = {

    id: 18,
    slug: 'smb-cybersecurity-data-protection-strategies',
    ogImage: '/og/courses/smb-cybersecurity-data-protection-strategies.jpg',
    courseCode: 'SMB-CYBER-DP-001',
    title: 'Securing Your SMB Data: Backup and Recovery Strategies',
    subtitle: 'Essential data protection and business continuity for SMBs',
    valueProposition: 'Protect your business from data loss with practical backup and recovery strategies covering ransomware resilience, disaster recovery planning, and testing procedures tailored for SMB budgets.',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'SMB-CYBER-DP-001 — Data backup and recovery covering 3-2-1 strategy, cloud and local storage, and disaster recovery planning for Australian SMBs.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 899
    },
    stripeConfig: {
    priceId: 'price_1T9bE9Ku1nyc322UirBQ3hTW',
    productId: 'prod_U7qwh7vgG2CWfc'
  },
    
    snapshot: {
      whoItsFor: [
        'SMB owners and managers',
        'IT staff and system administrators',
        'Anyone responsible for data management',
        'Business continuity planners'
      ],
      accreditation: 'Aligned to Essential Eight backup controls and ACSC data protection guidance'
    },
    
    logistics: {
      classSize: 'Maximum 20 participants for interactive exercises',
      requirements: [
        'Basic understanding of business systems and data',
        'Computer with internet access',
        'Access to information about your organisation\'s data and systems',
        'Authority to implement backup solutions (for decision-makers)'
      ]
    },
    
    overview: 'This practical 8-hour course focuses on the critical importance of data backup and recovery for SMBs facing ransomware, hardware failures, natural disasters, and human error. You\'ll learn to design, implement, and test backup strategies covering different backup methods (full, incremental, differential), storage options (cloud, local, hybrid), retention schedules, and disaster recovery planning. Through hands-on exercises and real case studies, you\'ll evaluate backup solutions suitable for SMB budgets, implement the 3-2-1 backup rule, protect backups from ransomware, and test recovery procedures to ensure they work when needed. Perfect for businesses that can\'t afford data loss but lack dedicated IT teams or enterprise-scale infrastructure.',
    
    outcomes: [
      'Understand different backup methods and when to use each approach',
      'Evaluate cloud, local, and hybrid backup solutions for SMB contexts',
      'Implement the 3-2-1 backup rule for data resilience',
      'Protect backups from ransomware and unauthorised access',
      'Design appropriate backup schedules and retention policies',
      'Test backup integrity and practice recovery procedures',
      'Create a disaster recovery plan for business continuity',
      'Calculate recovery time objectives (RTO) and recovery point objectives (RPO) for your business'
    ],
    
    audience: [
      'SMB owners and business managers',
      'IT staff and system administrators in small businesses',
      'Anyone responsible for data management and protection',
      'Business continuity and operations managers',
      'External IT consultants supporting SMB clients',
      'Office managers overseeing business systems'
    ],
    
    prerequisites: [
      'Basic understanding of business systems and data',
      'Familiarity with your organisation\'s data and file storage',
      'No technical expertise required—we cover concepts from foundational level'
    ],
    
    modules: [
      {
        title: 'Understanding Data Backup and Recovery Fundamentals',
        duration: '1 hour',
        topics: [
          'Why backups are critical for SMB survival',
          'Common causes of data loss: ransomware, hardware failure, human error, disasters',
          'Backup vs. archive vs. sync: Understanding the differences',
          'Recovery Time Objective (RTO) and Recovery Point Objective (RPO)',
          'The cost of data loss to Australian SMBs',
          'Real case studies of SMB data loss incidents',
          'Your legal obligations for data protection under Australian law'
        ]
      },
      {
        title: 'Backup Methods and Strategies',
        duration: '1.5 hours',
        topics: [
          'Full backups, incremental backups, and differential backups',
          'Understanding backup frequency and scheduling',
          'The 3-2-1 backup rule: 3 copies, 2 media types, 1 offsite',
          'Continuous data protection and snapshot-based backups',
          'Application-specific backups: databases, email, websites',
          'Backup retention policies and lifecycle management',
          'Hands-on exercise: Designing a backup strategy for your business'
        ]
      },
      {
        title: 'Cloud vs. Local Backup Solutions',
        duration: '1.5 hours',
        topics: [
          'Cloud backup advantages and disadvantages for SMBs',
          'Evaluating cloud backup providers and services',
          'Local backup options: NAS, external drives, servers',
          'Hybrid approaches combining cloud and local backups',
          'Cost comparison and budget considerations',
          'Bandwidth and storage capacity planning',
          'Australian data residency and privacy considerations',
          'Hands-on exercise: Evaluating backup solutions for your budget'
        ]
      },
      {
        title: 'Protecting Backups from Ransomware',
        duration: '1.5 hours',
        topics: [
          'How ransomware attacks backups and how to prevent it',
          'Immutable backups and air-gapped storage',
          'Securing backup credentials and access',
          'Network segmentation for backup systems',
          'Offline and offsite backup strategies',
          'Versioning and point-in-time recovery',
          'Essential Eight guidance on backing up important data',
          'Hands-on exercise: Hardening your backup infrastructure'
        ]
      },
      {
        title: 'Testing Backups and Disaster Recovery Planning',
        duration: '1.5 hours',
        topics: [
          'Why testing backups is essential (before you need them!)',
          'Recovery testing procedures and schedules',
          'Simulating disaster scenarios for practice',
          'Verifying backup integrity and completeness',
          'Documenting recovery procedures',
          'Creating a disaster recovery plan for your SMB',
          'Communication and coordination during recovery',
          'Hands-on exercise: Performing a test recovery'
        ]
      },
      {
        title: 'Implementing and Maintaining Your Backup Strategy',
        duration: '0.75 hours',
        topics: [
          'Selecting and implementing backup tools for SMBs',
          'Configuring automated backup schedules',
          'Monitoring backup success and failures',
          'Responding to backup failures and alerts',
          'Ongoing maintenance and health checks',
          'Updating backups as your business grows',
          'Training staff on backup responsibilities'
        ]
      },
      {
        title: 'Building Your Data Protection Action Plan',
        duration: '0.25 hours',
        topics: [
          'Creating your backup implementation checklist',
          'Prioritising backup initiatives based on criticality',
          'Budget planning for backup solutions',
          'Assigning roles and responsibilities',
          'Timeline for backup implementation and testing',
          'Next steps and ongoing improvement'
        ]
      }
    ],
    
    format: {
      type: 'Live webinar or self-paced online modules',
      includes: [
        '8 hours of practical instruction',
        'Backup strategy planning templates',
        'RTO and RPO assessment worksheets',
        'Disaster recovery plan template',
        'Backup solution comparison guides',
        'Testing checklists and procedures',
        'Cloud backup provider evaluation matrix',
        'Access to recorded sessions for 12 months',
        'Certificate of completion',
        'Email support for 60 days post-course'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Full-day live webinar (8 hours) or self-paced access available immediately'
    },
    
    faqs: [
      {
        question: 'Do we need technical skills to implement backups?',
        answer: 'No advanced technical skills required. We cover backup concepts from the ground up and focus on solutions suitable for non-technical users. Many modern backup tools are designed for small business owners to manage themselves with minimal IT expertise.'
      },
      {
        question: 'What backup solutions will be covered?',
        answer: 'We cover a range of solutions suitable for SMBs including cloud services (Microsoft 365, Google Workspace backups, Acronis, Backblaze), NAS devices (Synology, QNAP), external drives, and hybrid approaches. The focus is on principles—you can apply them to any backup tool.'
      },
      {
        question: 'How much will implementing proper backups cost?',
        answer: 'Costs vary widely based on data volume and approach. Simple cloud backups can start from $10-50/month for small businesses. We cover budget-conscious strategies and help you find solutions matching your financial constraints. Proper backups are far cheaper than recovering from data loss!'
      },
      {
        question: 'Is this course only about backups, or does it cover broader data protection?',
        answer: 'The course focuses primarily on backup and recovery strategies, which are foundational to data protection. We also cover disaster recovery planning, business continuity basics, and data protection obligations under Australian privacy law as they relate to backups.'
      },
      {
        question: 'Will this help us recover from ransomware?',
        answer: 'Yes—having secure, tested backups is the most effective defence against ransomware. The course extensively covers protecting backups from ransomware attacks and recovering your business without paying ransom. You\'ll learn to implement ransomware-resistant backup strategies.'
      },
      {
        question: 'How often should we test our backups?',
        answer: 'We recommend quarterly recovery testing at minimum, with annual full disaster recovery exercises. The course provides testing schedules and procedures appropriate to SMB resources. You\'ll learn to build testing into your ongoing operations without excessive overhead.'
      },
      {
        question: 'Does this course cover compliance requirements like Essential Eight?',
        answer: 'Yes, the Essential Eight framework includes "Backups" as a mitigation strategy. We cover Essential Eight backup requirements and how to meet them. You\'ll also learn about data protection obligations under Australian Privacy Principles (APPs) and retention requirements for your industry.'
      },
      {
        question: 'Can we get help implementing backups for our specific business after the course?',
        answer: 'Yes, we offer post-course implementation support and consulting. Many course participants engage us for hands-on help selecting and configuring backup solutions specific to their business. Email support is included for 60 days to answer implementation questions. Contact us for extended support options.'
      }
    ]
};
